package one.healthium.profile.navigation

import com.arkivanov.decompose.ComponentContext
import com.arkivanov.decompose.router.stack.ChildStack
import com.arkivanov.decompose.router.stack.StackNavigation
import com.arkivanov.decompose.router.stack.childStack
import com.arkivanov.decompose.router.stack.childStackWebNavigation
import com.arkivanov.decompose.router.stack.pop
import com.arkivanov.decompose.router.stack.pushNew
import com.arkivanov.decompose.router.webhistory.WebNavigation
import com.arkivanov.decompose.value.Value
import kotlinx.serialization.Serializable
import one.healthium.profile.navigation.food_history.DefaultFoodHistoryComponent
import one.healthium.profile.navigation.healthKit.DefaultHealthKitSettingsComponent
import one.healthium.profile.navigation.meal_plan_settings.DefaultMealPlanSettingsComponent
import one.healthium.profile.navigation.profile.DefaultProfileComponent
import one.healthium.profile.navigation.settingMenu.DefaultSettingsMenuComponent
import one.healthium.profile.navigation.settings.DefaultProfileSettingsComponent
import one.healthium.profile.navigation.weight_history.DefaultWeightHistoryComponent
import one.healthium.profile.ui.food_history.models.FoodItemUiModel
import kotlin.time.Instant

class DefaultProfileMainComponent(
    componentContext: ComponentContext,
    private val onContactUsClicked: suspend () -> Unit,
    private val onWeightHistoryClicked: (Float, Instant) -> Unit,
    private val onFoodHistoryItemClicked: (FoodItemUiModel) -> Unit,
    private val onUpdateNameClicked: () -> Unit,
    private val onUpdateAgeClicked: () -> Unit,
    private val onUpdateHeightClicked: () -> Unit,
    private val onUpdateInitialWeightClicked: () -> Unit,
    private val onUpdateTargetWeightClicked: () -> Unit,
) : ProfileMainComponent, ComponentContext by componentContext {

    private val nav = StackNavigation<Config>()

    private val _stack = childStack(
        source = nav,
        serializer = Config.serializer(),
        initialStack = { listOf(Config.Profile) },
        childFactory = ::child,
    )

    private fun child(config: Config, componentContext: ComponentContext): ProfileMainComponent.Child {
        return when (config) {
            Config.Profile -> ProfileMainComponent.Child.ProfileChild(
                DefaultProfileComponent(
                    componentContext = componentContext,
                    onSettingsClicked = ::onSettingsClicked,
                    onWeightProgressClicked = ::onWeightProgressClicked,
                    onCaloriesProgressClicked = ::onCaloriesProgressClicked,
                    onHealthKitClicked = ::onHealthKitClicked,
                )
            )

            Config.Settings -> ProfileMainComponent.Child.SettingsChild(
                DefaultProfileSettingsComponent(
                    componentContext = componentContext,
                    onNameClicked = onUpdateNameClicked,
                    onAgeClicked = onUpdateAgeClicked,
                    onHeightClicked = onUpdateHeightClicked,
                    onInitialWeightClicked = onUpdateInitialWeightClicked,
                    onTargetWeightClicked = onUpdateTargetWeightClicked,
                    onContactUsClicked = onContactUsClicked,
                    onSettingsMenuClicked = ::onSettingsMenuClicked,
                    onMealPlanClicked = ::onMealPlanClicked,
                    onHealthKitClicked = ::onHealthKitClicked,
                    onBackClicked = ::onBackClicked,
                )
            )

            Config.MealPlanSettings -> ProfileMainComponent.Child.MealPlanSettings(
                DefaultMealPlanSettingsComponent(
                    componentContext = componentContext,
                    onBackClicked = ::onBackClicked,
                )
            )

            is Config.SettingMenu -> ProfileMainComponent.Child.SettingsMenu(
                DefaultSettingsMenuComponent(
                    componentContext = componentContext,
                    menuId = config.menuId,
                    title = config.title,
                    onBackClicked = ::onBackClicked,
                )
            )

            is Config.WeightHistory -> ProfileMainComponent.Child.WeightHistory(
                DefaultWeightHistoryComponent(
                    componentContext = componentContext,
                    onBackClicked = ::onBackClicked,
                    onWeightHistoryClicked = onWeightHistoryClicked,
                )
            )

            is Config.FoodHistory -> ProfileMainComponent.Child.FoodHistory(
                DefaultFoodHistoryComponent(
                    componentContext = componentContext,
                    onBackClicked = ::onBackClicked,
                    onFoodItemClicked = onFoodHistoryItemClicked,
                )
            )

            is Config.HealthKitConnect -> ProfileMainComponent.Child.HealthKitConnect(
                DefaultHealthKitSettingsComponent(
                    componentContext = componentContext,
                    onBackClicked = ::onBackClicked,
                )
            )
        }
    }

    private fun onSettingsMenuClicked(menuId: String, title: String) {
        nav.pushNew(Config.SettingMenu(menuId = menuId, title = title))
    }

    private fun onMealPlanClicked() {
        nav.pushNew(Config.MealPlanSettings)
    }

    private fun onSettingsClicked() {
        nav.pushNew(Config.Settings)
    }

    private fun onWeightProgressClicked() {
        nav.pushNew(Config.WeightHistory)
    }

    private fun onCaloriesProgressClicked() {
        nav.pushNew(Config.FoodHistory)
    }

    private fun onHealthKitClicked() {
        nav.pushNew(Config.HealthKitConnect)
    }

    override val stack: Value<ChildStack<*, ProfileMainComponent.Child>>
        get() = _stack

    override val webNavigation: WebNavigation<*> =
        childStackWebNavigation(
            navigator = nav,
            stack = _stack,
            serializer = Config.serializer(),
        )

    override fun onBackClicked() = nav.pop()

    @Serializable
    private sealed interface Config {
        @Serializable
        data object Profile : Config

        @Serializable
        data object Settings : Config

        @Serializable
        data object MealPlanSettings : Config

        @Serializable
        data class SettingMenu(
            val menuId: String,
            val title: String,
        ) : Config

        @Serializable
        data object WeightHistory : Config

        @Serializable
        data object FoodHistory : Config

        @Serializable
        data object HealthKitConnect : Config
    }
}
